/**
 * What is left of the old deep-link resolution module, kept framework-free so
 * it can be unit-tested without React.
 *
 * DV-3012 removed the resolvers themselves (`resolveDirectOrgMatch`,
 * `resolveEntityViaTrees`) along with the cached-tree search and the two-shape
 * `OrgDataEntry`. Deep-link resolution is now `resolveEntityScope` in
 * `entity-scope-resolution.ts`, which walks ancestors instead of scanning every
 * accessible org's tree.
 *
 * One export survives: `toRoleSuffix`, the ONE role-suffix map (D14), used by
 * `tree-select.tsx`. `findEntityInApiTree` went with DV-3013 PR 4 — it had had
 * no production caller since `resolveEntityViaTrees` was deleted, because the
 * ancestor walk fetches entity docs instead of searching a tree.
 */

/**
 * UPPER_SNAKE role (as stored in localStorage) → the PascalCase suffix
 * `setUserContextAPI` expects in `groupName`.
 *
 * The ONE map (DV-3012 / D14). There were two: this one, and `formatRole`
 * inside `tree-select.tsx`, whose comment asked them to stay "in lockstep".
 * They did not — DV-2751 was that drift, and it silently returned
 * `CustomerAdmin` for post-processing users on the resolver path.
 *
 * Explicit entries, not a string transformation: the six roles below are the
 * only ones the backend has groups for. The other six internal roles
 * (INTERNAL_CLIENT_SUCCESS, INTERNAL_OPERATIONS, INTERNAL_FINANCE,
 * INTERNAL_DEVELOPER, INTERNAL_EXECUTIVE, PLANOTECH) have zero references in
 * backend code or UI today, so they fall back rather than minting a group name
 * that does not exist.
 */
const ROLE_SUFFIXES = {
  CUSTOMER_ADMIN: 'CustomerAdmin',
  CUSTOMER_USER: 'CustomerUser',
  CUSTOMER_FINANCE: 'CustomerFinance',
  CUSTOMER_DEVELOPER: 'CustomerDeveloper',
  INTERNAL_ADMIN: 'InternalAdmin',
  INTERNAL_POST_PROCESSING: 'InternalPostProcessing'
} as const satisfies Record<string, string>;

/** A realm role the UI supports: the keys of the one role table above. */
export type UiRole = keyof typeof ROLE_SUFFIXES;

export const isUiRole = (role: string): role is UiRole =>
  Object.keys(ROLE_SUFFIXES).includes(role);

export const toRoleSuffix = (role: string | null | undefined): string =>
  role && isUiRole(role) ? ROLE_SUFFIXES[role] : 'CustomerAdmin';

/**
 * The inverse: `CustomerAdmin` -> `CUSTOMER_ADMIN`.
 *
 * <p>DV-3573 review finding 1. `processTokenData` carried its own copy of this
 * mapping, which was fine while the token was the only thing that ever set
 * `role`. A switch now sets it without a token round trip, so the two needed a
 * shared table rather than two that can drift. Derived from `ROLE_SUFFIXES` so
 * adding a role to one direction cannot leave the other behind.
 */
const REALM_ROLES: Record<string, string> = Object.fromEntries(
  Object.entries(ROLE_SUFFIXES).map(([realm, suffix]) => [suffix, realm]),
);

export const toRealmRole = (suffix: string | null | undefined): string | null =>
  (suffix && REALM_ROLES[suffix]) || null;

/**
 * The realm role a `CurrentContext` claim (`<entity name><suffix>`) names, or
 * null — `processTokenData`'s login-time derivation, off the same table.
 *
 * Matched by `includes` in `ROLE_SUFFIXES` order, which is load-bearing:
 * INTERNAL_ADMIN is declared before INTERNAL_POST_PROCESSING, so if a context
 * ever carried both, admin wins — the backend's precedence in getAllEntities
 * (DV-2751).
 */
export const realmRoleInContext = (context: string | null | undefined): string | null => {
  if (!context) return null;
  const match = Object.entries(ROLE_SUFFIXES).find(([, suffix]) => context.includes(suffix));
  return match ? match[0] : null;
};
